import baseApi from "../api/baseApi.ts";
import type {
    BulkActionRequest,
    BulkActionResponse,
    IncidentListParams,
    PaginatedResponse,
    ThreatIntelRecordDetail,
    ThreatIntelRecordUpdate,
    ThreatStatsResponse
} from "../api/types.ts";

// Extend the base API with incident-specific endpoints
export const incidentApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // GET /intel/incidents/ - List all saved threat intelligence records for organization
        getIncidents: builder.query<
            PaginatedResponse<ThreatIntelRecordDetail>,
            IncidentListParams | void
        >({
            query: (params = {}) => ({
                url: '/intel/incidents/',
                method: 'GET',
                params,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.results.map(({ id }) => ({ type: 'Incident' as const, id })),
                        { type: 'Incident', id: 'LIST' },
                    ]
                    : [{ type: 'Incident', id: 'LIST' }],
        }),

        // GET /intel/incident/{id}/ - Get detailed view of a threat intelligence record
        getIncidentById: builder.query<ThreatIntelRecordDetail, string | number>({
            query: (id) => ({
                url: `/intel/incident/${id}/`,
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [{ type: 'Incident', id }],
        }),

        // PATCH /intel/incident/{id}/ - Update threat intelligence record (severity, status, tags, notes, source)
        updateIncident: builder.mutation<
            ThreatIntelRecordDetail,
            { id: string | number; data: ThreatIntelRecordUpdate }
        >({
            query: ({ id, data }) => ({
                url: `/intel/incident/${id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Incident', id },
                { type: 'Incident', id: 'LIST' },
                { type: 'Incident', id: 'STATS' }, // Invalidate stats when incident is updated
            ],
        }),

        // DELETE /intel/incident/{id}/ - Delete a threat intelligence record
        deleteIncident: builder.mutation<void, string | number>({
            query: (id) => ({
                url: `/intel/incident/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: 'Incident', id },
                { type: 'Incident', id: 'LIST' },
                { type: 'Incident', id: 'STATS' }, // Invalidate stats when incident is deleted
            ],
        }),

        // POST /intel/incident/bulk-actions/ - Perform bulk actions on threat intelligence records
        bulkActionIncidents: builder.mutation<BulkActionResponse, BulkActionRequest>({
            query: (data) => ({
                url: '/intel/incident/bulk-actions/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [
                { type: 'Incident', id: 'LIST' },
                { type: 'Incident', id: 'STATS' }, // Invalidate stats after bulk actions
            ],
        }),

        // GET /threats/stats/ - Get threat intelligence statistics for organization
        getThreatStats: builder.query<ThreatStatsResponse, void>({
            query: () => ({
                url: '/threats/stats/',
                method: 'GET',
            }),
            providesTags: [{ type: 'Incident', id: 'STATS' }],
        }),
    }),
    overrideExisting: false,
});

// Export hooks for usage in components
export const {
    useGetIncidentsQuery,
    useUpdateIncidentMutation,
    useDeleteIncidentMutation,
    useBulkActionIncidentsMutation,
    useGetThreatStatsQuery,
} = incidentApi;